import React, { useState } from 'react';
import { arrayOf, func, shape, string } from 'prop-types';
import { reportToSegment, types, eventNames } from '@smartcar/morse';
import { Box, Button, Typography } from '@mui/material';

import { dashboardRole as constants } from '../../scenes/Application/components/Members/utils/roles';
import applicationNameValidator from '../../services/validators/applicationNameValidator/applicationNameValidator';
import errors from '../../localization/Application/Members/errors';
import InputField from '../InputField';

const staticText = {
  description: 'Please enter an application name to get started.',
  button: 'Create',
  input: 'Application name',
  teamLabel: 'Team name',
};

// TODO: might need to consolidate with PermissionGate
const roleValidator = (role) => {
  if (!([constants.OWNER, constants.ADMIN].includes(role))) {
    throw new Error(errors.noPermissionToRegisterApplication);
  }
  return true;
};

const RegisterApplication = ({
  organization,
  applicationNames,
  toggleModal,
  actions: { registerApplication },
}) => {
  const [state, setState] = useState({
    name: '',
    isValid: false,
    attemptedSubmit: false,
    errorMessage: '',
  });

  const handleUpdateName = (event) => {
    const { value: name } = event.target;
    const { dashboardRole } = organization;
    try {
      roleValidator(dashboardRole);
      applicationNameValidator(name, applicationNames);
      setState(prev => ({
        ...prev, name, isValid: true, errorMessage: '',
      }));
    } catch (err) {
      setState(prev => ({
        ...prev, name, isValid: false, errorMessage: err.message,
      }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const { name, isValid, errorMessage } = state;

    if (isValid) {
      reportToSegment(types.TRACK, eventNames.formSubmitted, {
        label: 'register application',
        form_content: { name },
      });
      registerApplication({ name });
      toggleModal(false);
    } else {
      reportToSegment(types.TRACK, eventNames.formFailed, {
        label: 'register application',
        form_content: { name, errorMessage },
      });

      setState(prev => ({
        ...prev,
        attemptedSubmit: true,
      }));
    }
  };

  const showErrorMessage = state.attemptedSubmit && !state.isValid;
  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Typography variant="body1">{staticText.description}</Typography>
      <InputField
        label={staticText.teamLabel}
        inputValues={organization.name}
        inputReadOnly
        fullWidth
      />
      <form onSubmit={handleSubmit}>
        <InputField
          id="applicationName"
          inputName="applicationName"
          label={staticText.input}
          inputValues={state.name}
          handleChange={handleUpdateName}
          errorMessage={showErrorMessage && state.errorMessage}
          inputPlaceholder="e.g. Radiator Springs Fuel Delivery"
          required
          autoFocus
          fullWidth
          InputProps={{
            onKeyPress: (event) => {
              if (event.key === 'Enter') {
                handleSubmit(event);
              }
            },
            onBlur: () => {
              setState(prev => ({ ...prev, attemptedSubmit: true }));
            },
            onFocus: () => {
              setState(prev => ({ ...prev, attemptedSubmit: false }));
            },
          }}
        />
        <Box marginTop={3} display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="outlined"
            disabled={!state.isValid || !state.name}
          >
            {staticText.button}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default RegisterApplication;

RegisterApplication.propTypes = {
  actions: shape({
    registerApplication: func.isRequired,
  }).isRequired,
  toggleModal: func.isRequired,
  applicationNames: arrayOf(string).isRequired,
  organization: shape({
    name: string.isRequired,
    dashboardRole: string.isRequired,
  }).isRequired,
};
